/**
 * StepApprovalPrompt
 *
 * Renders an approval prompt for steps with ConsentLevel.REQUIRE_APPROVAL.
 * Shows step details and Approve / Reject / Request Changes actions.
 *
 * After approval, the step is automatically queued for execution.
 */

import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Chip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useMutation } from '@apollo/client';
import { PlanStep, ConsentLevel, ApprovalDecision } from '../../../types/generated/graphql';
import { SUBMIT_STEP_APPROVAL } from '../../../graphql/mutations';
import { GET_PLAN } from '../../../graphql/queries';
import { colors } from '../../../theme/colors';
import { useToast } from '../../../contexts/ToastContext';

interface StepApprovalPromptProps {
  planId: string;
  step: PlanStep;
}

export const StepApprovalPrompt: React.FC<StepApprovalPromptProps> = ({ planId, step }) => {
  const { showToast } = useToast();
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<ApprovalDecision | null>(null);

  const [submitApproval, { loading }] = useMutation(SUBMIT_STEP_APPROVAL, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    onCompleted: () => {
      showToast({
        message: pendingDecision === ApprovalDecision.Approved
          ? 'Step approved and queued for execution'
          : pendingDecision === ApprovalDecision.Rejected
            ? 'Step rejected'
            : 'Changes requested',
        severity: pendingDecision === ApprovalDecision.Approved ? 'success' : 'info',
        autoHideDuration: 4000,
      });
      setComment('');
      setShowComment(false);
      setPendingDecision(null);
    },
    onError: (err) => {
      showToast({
        message: err.message || 'Failed to submit approval',
        severity: 'error',
      });
      setPendingDecision(null);
    },
  });

  // Only show for steps requiring approval
  if (step.consentLevel !== ConsentLevel.RequireApproval) {
    return null;
  }

  // If already approved, show status
  const existingApproval = step.approvalRecords?.find(
    r => r.decision === ApprovalDecision.Approved
  );
  if (existingApproval) {
    return (
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
          label={`Approved ${existingApproval.decidedAt ? new Date(existingApproval.decidedAt).toLocaleDateString() : ''}`}
          size="small"
          sx={{
            bgcolor: colors.green[50],
            color: colors.green[700],
            fontWeight: 500,
          }}
        />
      </Box>
    );
  }

  const handleDecision = (decision: ApprovalDecision) => {
    if (decision !== ApprovalDecision.Approved) {
      setPendingDecision(decision);
      setShowComment(true);
      return;
    }
    setPendingDecision(decision);
    submitApproval({
      variables: { planId, stepId: step.id, decision, comment: comment || undefined },
    });
  };

  const handleSubmitWithComment = () => {
    if (!pendingDecision) return;
    submitApproval({
      variables: { planId, stepId: step.id, decision: pendingDecision, comment: comment || undefined },
    });
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${colors.amber[500]}`,
        bgcolor: colors.amber[50],
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: colors.amber[700], mb: 1 }}
      >
        Approval Required
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: colors.amber[600], mb: 1.5, fontSize: '0.8rem' }}
      >
        This step requires your approval before automation can proceed.
      </Typography>

      {!showComment ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleDecision(ApprovalDecision.Approved)}
            disabled={loading}
            sx={{
              bgcolor: colors.green[600],
              '&:hover': { bgcolor: colors.green[700] },
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {loading && pendingDecision === ApprovalDecision.Approved ? 'Approving...' : 'Approve & Run'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditNoteIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleDecision(ApprovalDecision.ChangesRequested)}
            disabled={loading}
            sx={{
              borderColor: colors.amber[400],
              color: colors.amber[700],
              '&:hover': { borderColor: colors.amber[600], bgcolor: colors.amber[100] },
              textTransform: 'none',
            }}
          >
            Request Changes
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleDecision(ApprovalDecision.Rejected)}
            disabled={loading}
            sx={{
              borderColor: colors.red[100],
              color: colors.red[700],
              '&:hover': { borderColor: colors.red[600], bgcolor: colors.red[50] },
              textTransform: 'none',
            }}
          >
            Reject
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            multiline
            rows={2}
            size="small"
            placeholder={
              pendingDecision === ApprovalDecision.Rejected
                ? 'Reason for rejection...'
                : 'What changes are needed?'
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            sx={{ bgcolor: 'white' }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmitWithComment}
              disabled={loading}
              sx={{
                bgcolor: pendingDecision === ApprovalDecision.Rejected ? colors.red[600] : colors.amber[600],
                '&:hover': {
                  bgcolor: pendingDecision === ApprovalDecision.Rejected ? colors.red[700] : colors.amber[700],
                },
                textTransform: 'none',
              }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => { setShowComment(false); setPendingDecision(null); setComment(''); }}
              disabled={loading}
              sx={{ textTransform: 'none', color: colors.gray[500] }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StepApprovalPrompt;
